/**
 * DiagramCard — renders a diagram from a note.
 *
 * `diagram` shape (from backend):
 *  { kind: "image" | "mermaid",
 *    title, description, labels: [],
 *    mermaid: "flowchart TD; A-->B",
 *    image_prompt: "…",
 *    image_data_url: "data:image/png;base64,…"  // populated post-gen for image kind
 *  }
 */
import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { Image as ImageIcon, GitBranch } from "lucide-react";
import { sanitiseMermaid, isValidMermaid } from "@/lib/latex-unicode";

// Initialise mermaid once. Neo-brutalist-friendly theme.
mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    primaryColor: "#DBEAFE",
    primaryTextColor: "#0A0A0A",
    primaryBorderColor: "#0A0A0A",
    lineColor: "#0A0A0A",
    secondaryColor: "#BFDBFE",
    tertiaryColor: "#F0F9FF",
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
    fontSize: "14px",
  },
  flowchart: { curve: "basis", padding: 12, htmlLabels: false },
  securityLevel: "strict",
});

let _mmSeq = 0;

function MermaidDiagram({ code }) {
  const ref = useRef(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cleaned = sanitiseMermaid(code);
      if (!ref.current || !cleaned) return;
      const id = `mm-${Date.now()}-${++_mmSeq}`;
      try {
        const { svg } = await mermaid.render(id, cleaned);
        if (!cancelled && ref.current) {
          ref.current.innerHTML = svg;
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (err) {
    // Diagram failed to parse — parent should have filtered it out via
    // `isRenderableDiagram()`. This fallback stays hidden.
    return null;
  }
  return <div ref={ref} className="mermaid-host w-full overflow-x-auto bg-white p-3" />;
}

/**
 * Async predicate: is this diagram usable? Called by the parent BEFORE render
 * so we can hide non-viable cards entirely (per user request — only show the
 * diagrams that actually formed).
 */
export async function isRenderableDiagram(diagram) {
  if (!diagram) return false;
  const kind = (diagram.kind || "").toLowerCase();
  if (kind === "image") {
    return !!diagram.image_data_url;
  }
  if (kind === "mermaid") {
    const cleaned = sanitiseMermaid(diagram.mermaid || "");
    if (!cleaned) return false;
    return await isValidMermaid(mermaid, cleaned);
  }
  return false;
}

export default function DiagramCard({ diagram }) {
  if (!diagram) return null;
  const kind = (diagram.kind || "").toLowerCase();
  const hasImage = kind === "image" && diagram.image_data_url;
  const hasMermaid = kind === "mermaid" && diagram.mermaid?.trim();

  return (
    <figure
      data-testid="diagram-card"
      className="border-2 border-black bg-white shadow-[3px_3px_0_0_#000]"
    >
      <div className="flex items-center gap-2 border-b-2 border-black bg-black text-white px-3 py-1.5">
        {hasImage ? (
          <ImageIcon className="w-3 h-3" />
        ) : (
          <GitBranch className="w-3 h-3" />
        )}
        <span className="font-mono text-[10px] uppercase tracking-widest truncate">
          {diagram.title || "Diagram"}
        </span>
      </div>

      {hasImage && (
        <img
          src={diagram.image_data_url}
          alt={diagram.title || "diagram"}
          className="w-full h-auto block bg-white"
          loading="lazy"
        />
      )}

      {hasMermaid && <MermaidDiagram code={diagram.mermaid} />}

      {!hasImage && !hasMermaid && (
        <div className="p-3 text-sm text-zinc-700">
          {diagram.description || "Diagram unavailable."}
        </div>
      )}

      {diagram.description && (hasImage || hasMermaid) && (
        <figcaption className="px-3 py-2 border-t border-black/10 text-xs text-zinc-700">
          {diagram.description}
        </figcaption>
      )}

      {diagram.labels?.length > 0 && (
        <div className="px-3 py-2 border-t border-black/10 flex flex-wrap gap-1">
          {diagram.labels.map((l, i) => (
            <span
              key={i}
              className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#DBEAFE] text-black"
            >
              {l}
            </span>
          ))}
        </div>
      )}
    </figure>
  );
}
